import { useEffect, useCallback, useRef } from 'react';
import config from '../config/config';
import { isMobileUa } from '../utils/detectDevice';

const DEFAULT_STYLE_NAME = config.map.defaultStyleName;
const TERRAIN_STYLE_NAME = config.map.styles.terrain;
const INITIAL_ZOOM = config.map.initialPosition.zoom;
const INITIAL_POSITION_LNG = config.map.initialPosition.center[0];
const INITIAL_POSITION_LAT = config.map.initialPosition.center[1];
const DEFAULT_PITCH = config.map.defaultPitch;
const DEFAULT_BEARING = config.map.defaultBearing;
const DEFAULT_MAP_MODE = config.map.defaultMapMode;
const COORD_PRECISION = config.map.defaultCoordPrecision;
const URL_SYNC_DEBOUNCE_MS = config.map.urlSyncDebounceMs;
const ZOOM_PRECISION = config.map.defaultZoomPrecision;
const PLACE_PREFIX = config.search.placePrefix;
const SEARCH_QUERY_PARAM = config.search.queryParam;
const SEARCH_POI_PARAM = config.search.poiParam;

// パス末尾の疑似パラメータ（.../key=value）からクエリへ移す対象キー
const MIGRATE_PATH_PARAMS = new Set([
  SEARCH_QUERY_PARAM,
  SEARCH_POI_PARAM,
]);

// モバイル端末で許容されているスタイル
const MOBILE_ALLOWED_STYLE_NAMES = new Set(
  [DEFAULT_STYLE_NAME, TERRAIN_STYLE_NAME].filter(Boolean)
);

const normalizeStyleNameForDevice = (styleName, isMobileDevice) => {
  if (!isMobileDevice) {
    return styleName;
  }
  return MOBILE_ALLOWED_STYLE_NAMES.has(styleName)
    ? styleName
    : DEFAULT_STYLE_NAME;
};

// URLから変換する地図の範囲、この外は無効な値とする
const VALID_RANGE = {
  minLat: config.map.validRange.minLat,
  maxLat: config.map.validRange.maxLat,
  minLng: config.map.validRange.minLng,
  maxLng: config.map.validRange.maxLng,
};

const isValidLatLng = (lat, lng) => {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= VALID_RANGE.minLat &&
    lat <= VALID_RANGE.maxLat &&
    lng >= VALID_RANGE.minLng &&
    lng <= VALID_RANGE.maxLng
  );
};

/**
 * URLのパス部分を解析
 */
export function parseRoute(pathname = '', options = {}) {
  /**
   * 旧形式URLがパス末尾に疑似パラメータを持つケースを、新形式（クエリ）に移す。
   * 例:
   *   /{lat},{lon},{zoom}/poi=213...  -> /{lat},{lon},{zoom}?poi=213...
   *   /{lat},{lon},{zoom}/q=東京      -> /{lat},{lon},{zoom}?q=東京
   */
  let pathnameForParse = pathname;

  if (typeof window !== 'undefined') {
    try {
      const params = new URLSearchParams(window.location.search);

      // パスをセグメント化（先頭/末尾スラッシュは除外）
      // 例: '/m2/35.6,139.7,15/q=東京' -> ['m2','35.6,139.7,15','q=東京']
      const segs = (pathnameForParse || '').split('/').filter(Boolean);
      const lastSeg = segs[segs.length - 1] || '';
      const eqIndex = lastSeg.indexOf('=');

      // 末尾が key=value 形式でなければ何もしない
      if (eqIndex > 0) {
        const key = lastSeg.slice(0, eqIndex);
        const rawValue = lastSeg.slice(eqIndex + 1);

        // 対象キー
        let isMigrateParam = false;
        if (MIGRATE_PATH_PARAMS.has(key)) {
          isMigrateParam = true;
        }

        // 既に同名クエリがあるなら上書きしない（意図しない上書きを避ける）
        if (isMigrateParam && !params.has(key)) {
          // 値をdecode（画面側は人間が読める文字列を期待）
          let value = rawValue;
          try {
            value = decodeURIComponent(rawValue);
          } catch (e) {
          }
          // クエリへ移す
          params.set(key, value);
          // パス末尾から疑似パラメータを除去
          segs.pop();

          // クエリを維持したままURLを書き換える（リロードはしない）
          const cleanedPathname = '/' + segs.join('/');
          const query = params.toString();
          const newUrl = query ? `${cleanedPathname}?${query}` : cleanedPathname;
          const currentUrl = window.location.pathname + window.location.search;

          if (newUrl !== currentUrl) {
            try {
              // URLを書き換えるところ（Safariで稀にHistory API制限系の例外が出ることがある）
              window.history.replaceState(null, '', newUrl);
            } catch (e) {
            }
          }

          // 以後の解析は「掃除済みパス」で行う（parts に q=... 等が混ざるのを防ぐ）
          pathnameForParse = cleanedPathname;
        }
      }
    } catch (e) {
    }
  }

  // サブディレクトリ配置対応: PUBLIC_URL（例: /m2）を基底パスとして取得
  // window.location.pathname が '/m2/14/35.681236/139.767125/0/0' の場合、
  // 基底パス '/m2' を除去して '/14/35.681236/139.767125/0/0' として処理する必要がある
  const baseUrl = process.env.PUBLIC_URL || '';
  const basePath = baseUrl.replace(/\/$/, ''); // 末尾のスラッシュを削除

  // pathnameから基底パス（/m2）を除去
  let cleanPath = pathnameForParse;
  if (basePath && pathnameForParse.startsWith(basePath)) {
    cleanPath = pathnameForParse.substring(basePath.length);
  }

  // 先頭のスラッシュを除去してパス文字列を正規化
  // 例: '/14/35.681236/139.767125/0/0' → '14/35.681236/139.767125/0/0'
  const raw = cleanPath.startsWith('/') ? cleanPath.slice(1) : cleanPath;
  const normalized = raw.replace(/^index\.html\/?/, '').replace(/\/index\.html\/?/, '/').replace(/\/$/, '');
  const parts = normalized.split('/').filter(Boolean);

  // /place/keyword の場合
  if (parts.length > 0 && parts[0] === PLACE_PREFIX) {
    return {
      isPlaceSearch: true,
      keyword: parts[1] || '',
      mapConfig: {
        style: config.api.map.styles(DEFAULT_STYLE_NAME),
        styleName: DEFAULT_STYLE_NAME,
        center: { lng: INITIAL_POSITION_LNG, lat: INITIAL_POSITION_LAT },
        zoom: INITIAL_ZOOM,
        pitch: DEFAULT_PITCH,
        bearing: DEFAULT_BEARING,
        hasUrlCoords: false,
        mode: DEFAULT_MAP_MODE
      }
    };
  }

  // 旧形式 /{lat},{lon},{zoom} のURLを /{zoom}/{lat}/{lon}/0/0に変換（例: /35.67,139.75,10）
  // ここでのparts[0]は新形式ではstyle名またはズームレベルなので、カンマ区切りの3要素なら旧形式と判断できる
  if (parts.length > 0 && typeof parts[0] === 'string' && parts[0].includes(',')) {
    // lat,lon,zoomがカンマ区切りなので分解
    const nums = parts[0].split(',').map(s => parseFloat(String(s).trim()));
    // 上記の分の3要素があって全て数値であること
    if (nums.length >= 3 && nums.every(n => Number.isFinite(n))) {
      const [lat, lng, zoom] = nums;
      // この際にzoomレベルを、ラスター→ベクターで1つ差があることを踏まえて−1する
      const zoomForVector = zoom - 1;
      // styleは旧形式に存在しないもの、ここではデフォルトを指定
      const resolvedStyleUrl = config.api.map.styles(DEFAULT_STYLE_NAME);
      const hasUrlCoords = isValidLatLng(lat, lng);
      return {
        isPlaceSearch: false,
        mapConfig: {
          style: resolvedStyleUrl,
          styleName: DEFAULT_STYLE_NAME,
          center: hasUrlCoords
            ? { lat, lng }
            : { lat: INITIAL_POSITION_LAT, lng: INITIAL_POSITION_LNG },
          zoom: zoomForVector,
          // pitchとbearingも旧形式には存在しないもの、デフォルト値を使用
          pitch: DEFAULT_PITCH,
          bearing: DEFAULT_BEARING,
          // URL由来の座標が有効ならtrue
          hasUrlCoords,
          mode: DEFAULT_MAP_MODE
        }
      };
    }
  }

  // 基本形 /(style/)zoom/lat/lng(/pitch)(/bearing) 形式
  // モバイルの場合、参照できるスタイルに制限がある
  const isMobileDevice = typeof options.isMobileDevice === 'boolean'
    ? options.isMobileDevice
    : isMobileUa();

  let styleName = DEFAULT_STYLE_NAME;
  let zoomIndex = 0;
  if (parts.length > 0 && isNaN(parseFloat(parts[0]))) {
    styleName = parts[0];
    zoomIndex = 1;
  }

  // モバイルで許容されていないスタイルがURLで持たれていた場合はデフォルトスタイルに置き換える
  styleName = normalizeStyleNameForDevice(styleName, isMobileDevice);

  const zoom = parseFloat(parts[zoomIndex]);
  const lat = parseFloat(parts[zoomIndex + 1]);
  const lng = parseFloat(parts[zoomIndex + 2]);
  const pitch = parseFloat(parts[zoomIndex + 3]);
  const bearing = parseFloat(parts[zoomIndex + 4]);
  const hasUrlCoords = isValidLatLng(lat, lng);

  const resolvedStyleUrl = config.api.map.styles(styleName);
  const defaultStyleUrl = config.api.map.styles(DEFAULT_STYLE_NAME);
  const resolvedStyleName = (resolvedStyleUrl === defaultStyleUrl) ? DEFAULT_STYLE_NAME : styleName;

  return {
    isPlaceSearch: false,
    mapConfig: {
      style: resolvedStyleUrl || defaultStyleUrl,
      styleName: resolvedStyleName,
      center: hasUrlCoords
        ? { lat, lng }
        : { lat: INITIAL_POSITION_LAT, lng: INITIAL_POSITION_LNG },
      zoom: !isNaN(zoom) ? zoom : INITIAL_ZOOM,
      pitch: !isNaN(pitch) ? pitch : DEFAULT_PITCH,
      bearing: !isNaN(bearing) ? bearing : DEFAULT_BEARING,
      hasUrlCoords,
      mode: DEFAULT_MAP_MODE
    }
  };
}

/**
 * カスタムフック：URLと地図状態の同期
 */
export function useUrlSync(
  map,
  onCenterChange,
  currentStyleName,
  getCurrentSearchQuery
) {
  const currentStyleRef = useRef(currentStyleName);
  useEffect(() => {
    currentStyleRef.current = currentStyleName;
  }, [currentStyleName]);

  // moveendの連発をまとめる（末尾debounce）
  const moveendTimerRef = useRef(null);

  // 地図移動時にURL更新
  const updateUrl = useCallback(() => {
    if (!map) {
      return;
    }

    const center = map.getCenter();
    const zoom = Math.round(map.getZoom() * 100) / 100;
    const pitch = Math.round(map.getPitch());
    const bearing = Math.round(map.getBearing());

    // デフォルト以外のスタイルの場合はパスにスタイル名を含める
    let stylePrefix = '';
    if (currentStyleRef.current !== DEFAULT_STYLE_NAME) {
      stylePrefix = `${currentStyleRef.current}/`;
    }

    // サブディレクトリ配置対応: URL生成時に基底パス（例: /m2）を付与
    // parseRoute() で除去した基底パスをここで再付与することで、
    // '/m2/14/35.681236/139.767125/0/0' のような正しいURLを生成する
    const baseUrl = process.env.PUBLIC_URL || '';
    const basePath = baseUrl.replace(/\/$/, ''); // 末尾のスラッシュを削除

    // 完全なパスを構築（基底パス + スタイル + 座標情報）
    const pathOnly = `${basePath}/${stylePrefix}${zoom.toFixed(ZOOM_PRECISION)}/${center.lat.toFixed(COORD_PRECISION)}/${center.lng.toFixed(COORD_PRECISION)}/${pitch}/${bearing}`;
    // クエリパラメータの構築
    const params = new URLSearchParams(window.location.search);
    const currentQuery = getCurrentSearchQuery();

    // 通常、検索を行う場合にURLにqパラメータを付与するが、
    // poiがある場合はpoiを優先させ、qを付けない。1件ヒットする検索でURLを安定させる
    if (params.has(SEARCH_POI_PARAM)) {
      params.delete(SEARCH_QUERY_PARAM);
      // 検索クエリが存在する場合はqパラメータを付与
    } else if (currentQuery) {
      params.set(SEARCH_QUERY_PARAM, currentQuery);
      // その他は無効なパラメータとして除去
    } else {
      params.delete(SEARCH_QUERY_PARAM);
    }

    const queryString = params.toString();
    const newPath = queryString ? `${pathOnly}?${queryString}` : pathOnly;
    const currentFullPath = window.location.pathname + window.location.search;


    if (currentFullPath !== newPath) {
      try {
        // URLを書き換えるところ、主にSafariで回数制限エラーのリスク箇所
        window.history.replaceState(null, '', newPath);
      } catch (e) {
        // Safariで稀に発生する「History APIの呼び出し回数制限エラー」を無視する
        // これが発生してもURLが古いままになるだけで、機能的には大きな問題はないため
      }
    }

    onCenterChange({ lat: center.lat, lng: center.lng });
  }, [map, onCenterChange, getCurrentSearchQuery]);

  useEffect(() => {
    if (!map) {
      return;
    }

    // popstate時はリロードしてAppの初期化に任せる
    const handlePopState = () => window.location.reload();
    window.addEventListener('popstate', handlePopState);

    // moveendで即updateUrlせず、末尾debounceでまとめる
    const scheduleUpdateUrl = () => {
      if (moveendTimerRef.current) {
        clearTimeout(moveendTimerRef.current);
      }
      moveendTimerRef.current = setTimeout(() => {
        updateUrl();
      }, URL_SYNC_DEBOUNCE_MS);
    };

    // ページを離れる/非表示になる直前に最後のURL同期を確実にする
    const flushUpdateUrl = () => {
      if (moveendTimerRef.current) {
        clearTimeout(moveendTimerRef.current);
        moveendTimerRef.current = null;
      }
      updateUrl();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushUpdateUrl();
      }
    };

    map.on('moveend', scheduleUpdateUrl);
    window.addEventListener('pagehide', flushUpdateUrl);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // map.off('moveend', handleMapMove);
      map.off('moveend', scheduleUpdateUrl);

      // 追加したイベントを掃除
      window.removeEventListener('pagehide', flushUpdateUrl);
      document.removeEventListener('visibilitychange', onVisibilityChange);

      // タイマー掃除
      if (moveendTimerRef.current) {
        clearTimeout(moveendTimerRef.current);
        moveendTimerRef.current = null;
      }
    };
  }, [map, updateUrl]);

  useEffect(() => {
    if (!map) return;
    updateUrl();
  }, [currentStyleName, map, updateUrl]);
}

// poiパラメータをURLから除去する関数
export function removePoiParam() {
  const params = new URLSearchParams(window.location.search);
  if (!params.has(SEARCH_POI_PARAM)) {
    return;
  }
  params.delete(SEARCH_POI_PARAM);

  const queryString = params.toString();
  const newPath = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname;

  const currentFullPath = window.location.pathname + window.location.search;

  if (currentFullPath !== newPath) {
    try {
      // URLを書き換えるところ、主にSafariで回数制限エラーのリスク箇所
      window.history.replaceState(null, '', newPath);
    } catch (e) {
      // Safariで稀に発生する「History APIの呼び出し回数制限エラー」を無視する
      // これが発生してもURLが古いままになるだけで、機能的には大きな問題はないため
    }
  }
}
